import { useMemo } from 'react';
import type { LeadPayload } from '../../services/leads';

type LeadWithMeta = LeadPayload & { id: string; createdAt?: Date };

type LeadAnalyticsProps = {
  leads: LeadWithMeta[];
};

export function LeadAnalytics({ leads }: LeadAnalyticsProps) {
  const stats = useMemo(() => {
    const total = leads.length;
    const byStatus = {
      new: leads.filter((l) => l.status === 'new').length,
      in_progress: leads.filter((l) => l.status === 'in_progress').length,
      done: leads.filter((l) => l.status === 'done').length,
    };

    const byRole = leads.reduce<Record<string, number>>((acc, lead) => {
      acc[lead.role] = (acc[lead.role] ?? 0) + 1;
      return acc;
    }, {});

    const byOrdine = leads.reduce<Record<string, number>>((acc, lead) => {
      if (lead.ordine) {
        acc[lead.ordine] = (acc[lead.ordine] ?? 0) + 1;
      }
      return acc;
    }, {});

    const interestCounts = leads.reduce<Record<string, number>>((acc, lead) => {
      lead.interestLabels?.forEach((interest) => {
        acc[interest] = (acc[interest] ?? 0) + 1;
      });
      return acc;
    }, {});

    const topInterests = Object.entries(interestCounts)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 5);

    const consentMarketing = leads.filter((l) => l.consentMarketing).length;
    const consentPrivacy = leads.filter((l) => l.consentPrivacy).length;

    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const weekAgo = new Date(today);
    weekAgo.setDate(weekAgo.getDate() - 7);
    const monthAgo = new Date(today);
    monthAgo.setMonth(monthAgo.getMonth() - 1);

    const leadsToday = leads.filter((l) => l.createdAt && l.createdAt >= today).length;
    const leadsWeek = leads.filter((l) => l.createdAt && l.createdAt >= weekAgo).length;
    const leadsMonth = leads.filter((l) => l.createdAt && l.createdAt >= monthAgo).length;

    return {
      total,
      byStatus,
      byRole,
      byOrdine,
      topInterests,
      consentMarketing,
      consentPrivacy,
      leadsToday,
      leadsWeek,
      leadsMonth,
    };
  }, [leads]);

  return (
    <div className="space-y-6">
      <section>
        <h2 className="text-xl font-semibold mb-3 text-slate-100">Panoramica generale</h2>
        <div className="grid md:grid-cols-4 gap-4">
          <div className="bg-slate-900 border border-slate-800 rounded-lg p-4">
            <p className="text-slate-400 text-sm">Lead totali</p>
            <p className="text-3xl font-semibold text-slate-50">{stats.total}</p>
          </div>
          <div className="bg-slate-900 border border-slate-800 rounded-lg p-4">
            <p className="text-slate-400 text-sm">Oggi</p>
            <p className="text-3xl font-semibold text-blue-400">{stats.leadsToday}</p>
          </div>
          <div className="bg-slate-900 border border-slate-800 rounded-lg p-4">
            <p className="text-slate-400 text-sm">Ultimi 7 giorni</p>
            <p className="text-3xl font-semibold text-indigo-400">{stats.leadsWeek}</p>
          </div>
          <div className="bg-slate-900 border border-slate-800 rounded-lg p-4">
            <p className="text-slate-400 text-sm">Ultimo mese</p>
            <p className="text-3xl font-semibold text-purple-400">{stats.leadsMonth}</p>
          </div>
        </div>
      </section>

      <section>
        <h2 className="text-xl font-semibold mb-3 text-slate-100">Stato lavorazione</h2>
        <div className="grid md:grid-cols-3 gap-4">
          <div className="bg-slate-900 border border-slate-800 rounded-lg p-4">
            <p className="text-slate-400 text-sm">Nuovi</p>
            <p className="text-2xl font-semibold text-blue-300">{stats.byStatus.new}</p>
          </div>
          <div className="bg-slate-900 border border-slate-800 rounded-lg p-4">
            <p className="text-slate-400 text-sm">In lavorazione</p>
            <p className="text-2xl font-semibold text-yellow-300">{stats.byStatus.in_progress}</p>
          </div>
          <div className="bg-slate-900 border border-slate-800 rounded-lg p-4">
            <p className="text-slate-400 text-sm">Completati</p>
            <p className="text-2xl font-semibold text-green-300">{stats.byStatus.done}</p>
          </div>
        </div>
      </section>

      <section>
        <h2 className="text-xl font-semibold mb-3 text-slate-100">Distribuzione per ruolo</h2>
        <div className="bg-slate-900 border border-slate-800 rounded-lg p-4">
          <ul className="space-y-2">
            {Object.entries(stats.byRole).map(([role, count]) => (
              <li key={role} className="flex justify-between text-sm">
                <span className="text-slate-300">{role}</span>
                <span className="font-semibold text-slate-100">{count}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {Object.keys(stats.byOrdine).length > 0 && (
        <section>
          <h2 className="text-xl font-semibold mb-3 text-slate-100">Distribuzione per ordine scolastico</h2>
          <div className="bg-slate-900 border border-slate-800 rounded-lg p-4">
            <ul className="space-y-2">
              {Object.entries(stats.byOrdine).map(([ordine, count]) => (
                <li key={ordine} className="flex justify-between text-sm">
                  <span className="text-slate-300">{ordine}</span>
                  <span className="font-semibold text-slate-100">{count}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      <section>
        <h2 className="text-xl font-semibold mb-3 text-slate-100">Top 5 interessi</h2>
        <div className="bg-slate-900 border border-slate-800 rounded-lg p-4">
          <ul className="space-y-2">
            {stats.topInterests.map(([interest, count]) => (
              <li key={interest} className="flex justify-between text-sm">
                <span className="text-slate-300">{interest}</span>
                <span className="font-semibold text-slate-100">{count}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section>
        <h2 className="text-xl font-semibold mb-3 text-slate-100">Consensi</h2>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="bg-slate-900 border border-slate-800 rounded-lg p-4">
            <p className="text-slate-400 text-sm">Consenso marketing</p>
            <p className="text-2xl font-semibold text-slate-100">
              {stats.consentMarketing} / {stats.total}
            </p>
            <p className="text-xs text-slate-500 mt-1">
              {stats.total > 0 ? ((stats.consentMarketing / stats.total) * 100).toFixed(1) : 0}%
            </p>
          </div>
          <div className="bg-slate-900 border border-slate-800 rounded-lg p-4">
            <p className="text-slate-400 text-sm">Consenso privacy</p>
            <p className="text-2xl font-semibold text-slate-100">
              {stats.consentPrivacy} / {stats.total}
            </p>
            <p className="text-xs text-slate-500 mt-1">
              {stats.total > 0 ? ((stats.consentPrivacy / stats.total) * 100).toFixed(1) : 0}%
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
